import {
  seasonWeeks,
  weekRuns,
  type Grower,
  type HarvestWeek,
  type Season,
  type UserFarmRow,
} from '@fruitways/shared';
import { api } from '@/api/endpoints';
import { KV, setKv } from '@/db/database';
import { pullAdminData } from '@/db/sync';
import { useAuth } from '@/hooks/useAuth';
import { useKv } from '@/hooks/useMasterData';

/** The admin interface shows only for role admin. Growers and staff keep the original app. */
export function useIsAdmin(): boolean {
  return useAuth().user?.role === 'admin';
}

export const useGrowers = () => useKv<Grower[]>(KV.adminGrowers);
export const useAdminHarvestWeeks = () => useKv<HarvestWeek[]>(KV.adminHarvestWeeks);
export const useAdminUserFarms = () => useKv<UserFarmRow[]>(KV.adminUserFarms);

/** The grower a farm belongs to (first match; a farm has one grower in practice). */
export function growerOf(
  growers: readonly Grower[] | undefined,
  farmId: string | null | undefined,
) {
  if (!growers || !farmId) return undefined;
  return growers.find((g) => g.farmIds.includes(farmId));
}

/** After any admin change: refresh the admin tables and the snapshot capture reads from. */
export async function refreshAfterAdminChange(): Promise<void> {
  const [master] = await Promise.all([api.masterData(), pullAdminData()]);
  await setKv(KV.masterData, master);
}

/** Index of each week number in season order, for sorting (week 52, 53, then 1, 2 …). */
export function seasonOrder(season: Season | undefined): Map<number, number> {
  if (!season) return new Map();
  return new Map(seasonWeeks(season.startDate, season.endDate).map((w, i) => [w.weekNumber, i]));
}

/** Week numbers per variety for one season, each list in season order. One pass over the rows. */
export function weeksByVariety(
  rows: readonly HarvestWeek[] | undefined,
  season: Season | undefined,
): Map<string, number[]> {
  const out = new Map<string, number[]>();
  if (!rows || !season) return out;
  const order = seasonOrder(season);
  for (const r of rows) {
    if (r.seasonId !== season.id) continue;
    out.set(r.varietyCode, [...(out.get(r.varietyCode) ?? []), r.weekNumber]);
  }
  for (const ws of out.values()) ws.sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99));
  return out;
}

export { weekRuns };
